// BM-H4006 - the digital assets screen warns BEFORE saving, and the questionnaire never sends text that looks like an
// access credential (a password, key or recovery phrase) to the server.
//
// This is a port of `credential_reason` in backend/hub/wills_assembly/digital_assets.py, the check the Will assembler and the
// save route use. The server is the authority (it refuses the save with a 422 whatever the browser does); this copy exists so
// the client is told at once, and so the text does not even leave the browser. A shared corpus
// (__tests__/wills/fixtures/digitalCredentialCorpus.json) is run through BOTH, so they cannot drift apart unnoticed.
//
// Nothing here logs, stores or returns the text: only yes or no, and the positions of the holdings concerned.

const EXEMPT = /\bpass(?:word|code|phrase)[\s-]+(?:manager|vault|safe|protected|reset|policy)\b/gi;

const KEYWORD_PATTERNS = [
  /\bpass(?:word|wd|code|phrase)s?\b/i,
  /\bpass[\s-]+(?:word|code|phrase)s?\b/i,
  /\bpin\b/i,
  /\b(?:private|secret|signing|master)[\s-]*keys?\b/i,
  /\bmnemonic\b/i,
  /\b(?:seed|recovery|backup|secret|wallet)[\s-]*(?:phrases?|words|seeds?|keys?|codes?)\b/i,
  /\b(?:2fa|mfa|otp|one[\s-]*time|verification|authenticat(?:ion|or)|security|auth)[\s-]*(?:codes?|tokens?|pins?)\b/i,
  /\b(?:access|auth|bearer|refresh|api|oauth|session|personal[\s-]access)[\s-]*(?:tokens?|keys?|secrets?)\b/i,
  /\bclient[\s-]*secret\b/i,
  /\b(?:pw|pwd|passwd|pass|secret|token|apikey|passcode)\s*[:=]\s*\S/i,
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/i,
  /\b(?:sk_live_|sk-[A-Za-z0-9]|ghp_|gho_|AKIA[0-9A-Z]{16}|xox[abp]-)/i,
  /-----BEGIN [A-Z ]*PRIVATE KEY/i,
];
const HEX = /^(?:0x)?[0-9a-fA-F]{32,}$/;
const ETH_ADDRESS = /^0x[0-9a-fA-F]{40}$/; // a public Ethereum address: not secret, and useful for finding a wallet
const WIF = /^[5KL][1-9A-HJ-NP-Za-km-z]{50,51}$/;
const XPRV = /^[xyztu]prv[1-9A-HJ-NP-Za-km-z]{50,}$/;
const LONG_TOKEN = /^[A-Za-z0-9+/=_-]{32,}$/;
const BIP39_SHAPE = /^[a-z]{3,8}$/;
const SHORT_STOPWORDS = new Set("the and of with my to for in on is are at by from that this its our your their which not".split(" "));

export function looksLikeCredential(text) {
  if (typeof text !== "string" || !text.trim()) return false;
  const cleaned = text.replace(EXEMPT, " ");
  if (KEYWORD_PATTERNS.some((p) => p.test(cleaned))) return true;
  for (let token of text.trim().split(/[\s,;|]+/)) {
    token = token.replace(/^["'()[\]{}<>.:]+|["'()[\]{}<>.:]+$/g, "");
    if (!token) continue;
    if (ETH_ADDRESS.test(token)) continue;
    if (HEX.test(token) || WIF.test(token) || XPRV.test(token)) return true;
    if (LONG_TOKEN.test(token) && /[0-9]/.test(token) && /[A-Za-z]/.test(token)) return true;
  }
  const words = (text.match(/[A-Za-z0-9]+/g) || []).filter((w) => !/^\d+$/.test(w));
  for (let i = 0; i + 12 <= words.length; i += 1) {
    const window = words.slice(i, i + 12);
    if (window.every((w) => BIP39_SHAPE.test(w)) && window.filter((w) => SHORT_STOPWORDS.has(w)).length <= 1) return true;
  }
  return false;
}

export const CREDENTIAL_FIELDS = ["provider", "description"];

function* digitalSections(node, trail = [], depth = 0) {
  if (depth > 8 || node === null || typeof node !== "object" || Array.isArray(node)) return;
  for (const [key, value] of Object.entries(node)) {
    if (key === "s8_digital" && value && typeof value === "object" && !Array.isArray(value)) {
      yield [[...trail, key], value];
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      yield* digitalSections(value, [...trail, key], depth + 1);
    }
  }
}

// [{ whose: "your" | "your partner's", position: 2, field: "provider" }] for every holding field that looks like a credential,
// found anywhere in an answers object (the questionnaire's own, the mirror partner's, the stashed mirror answers).
export function credentialPositions(answers) {
  const found = [];
  for (const [trail, section] of digitalSections(answers)) {
    if (!Array.isArray(section.holdings)) continue;
    const joined = trail.join("/");
    const whose = joined.includes("mirror_partner") || joined.includes("b_stashed") ? "your partner's" : "your";
    section.holdings.forEach((h, i) => {
      if (!h || typeof h !== "object") return;
      CREDENTIAL_FIELDS.forEach((field) => {
        if (looksLikeCredential(h[field])) found.push({ whose, position: i + 1, field });
      });
    });
  }
  return found;
}

export const CREDENTIAL_FIELD_WARNING =
  "This looks like a password, key or recovery phrase. It will not be saved. Please delete it and describe the account and where it is held instead.";

export const CREDENTIAL_SAVE_BLOCKED_MESSAGE =
  "Something on the Digital assets screen looks like a password, key or recovery phrase, so your latest changes have not been saved. Please delete it and describe the account and where it is held instead.";
